// SPDX-License-Identifier: AGPL-3.0-or-later

import {Button} from '@app/features/ui/button/Button';
import styles from '@app/features/ui/components/Slider.module.css';
import type {SliderResetButtonPlacement} from '@app/features/ui/components/slider/SliderTypes';
import {ArrowCounterClockwiseIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import type React from 'react';

interface SliderResetControlsProps {
	placement: SliderResetButtonPlacement;
	canReset: boolean;
	onReset: () => void;
	inlineLabel: string;
	label: React.ReactNode;
	accessory?: React.ReactNode;
	children: React.ReactNode;
}

export function SliderResetControls({
	placement,
	canReset,
	onReset,
	inlineLabel,
	label,
	accessory,
	children,
}: SliderResetControlsProps): React.ReactElement {
	if (placement === 'below') {
		return (
			<>
				{children}
				{canReset || accessory ? (
					<div className={styles.resetRow} data-flx="ui.slider.slider-reset-controls.reset-row">
						{canReset ? (
							<Button
								variant="secondary"
								small
								fitContent
								onClick={onReset}
								leftIcon={
									<ArrowCounterClockwiseIcon
										size={14}
										weight="bold"
										data-flx="ui.slider.slider-reset-controls.arrow-counter-clockwise-icon"
									/>
								}
								data-flx="ui.slider.slider-reset-controls.button.reset"
							>
								{label}
							</Button>
						) : null}
						{accessory ? (
							<span className={styles.resetAccessory} data-flx="ui.slider.slider-reset-controls.reset-accessory">
								{accessory}
							</span>
						) : null}
					</div>
				) : null}
			</>
		);
	}
	return (
		<div className={styles.sliderRow} data-flx="ui.slider.slider-reset-controls.slider-row">
			<div className={styles.sliderRowMain} data-flx="ui.slider.slider-reset-controls.slider-row-main">
				{children}
			</div>
			<button
				type="button"
				className={clsx(styles.resetButton, !canReset && styles.resetButtonHidden)}
				onClick={onReset}
				disabled={!canReset}
				aria-label={inlineLabel}
				tabIndex={canReset ? 0 : -1}
				data-flx="ui.slider.slider-reset-controls.reset-button"
			>
				<ArrowCounterClockwiseIcon
					size={14}
					weight="bold"
					data-flx="ui.slider.slider-reset-controls.arrow-counter-clockwise-icon--2"
				/>
			</button>
		</div>
	);
}
